import { Capacitor } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';

/**
 * Deep Linking Service
 *
 * Handles URL scheme (sunsquatcher://) and Universal Links for iOS/Android.
 * Routes deep links to appropriate app screens.
 */

const APP_URL_SCHEME = 'sunsquatcher';
const WEB_DOMAIN = 'app.sunsquatcher.com';

export interface DeepLinkRoute {
  path: string;
  params: Record<string, string>;
}

export type DeepLinkHandler = (route: DeepLinkRoute) => void;

const isNative = Capacitor.isNativePlatform();

let deepLinkHandler: DeepLinkHandler | null = null;
let isInitialized = false;
let pendingDeepLink: URLOpenListenerEvent | null = null;

/**
 * Parse a deep link URL into a route object
 */
export function parseDeepLink(url: string): DeepLinkRoute | null {
  try {
    let pathname: string;
    let searchParams: URLSearchParams;

    // Handle custom URL scheme (sunsquatcher://path?params)
    if (url.startsWith(`${APP_URL_SCHEME}://`)) {
      const withoutScheme = url.replace(`${APP_URL_SCHEME}://`, '');
      const [path, query] = withoutScheme.split('?');
      pathname = `/${path}`;
      searchParams = new URLSearchParams(query || '');
    }
    // Handle universal links (https://app.sunsquatcher.com/path?params)
    else if (url.includes(WEB_DOMAIN)) {
      const parsedUrl = new URL(url);
      pathname = parsedUrl.pathname;
      searchParams = parsedUrl.searchParams;
    }
    // Handle relative paths
    else if (url.startsWith('/')) {
      const [path, query] = url.split('?');
      pathname = path;
      searchParams = new URLSearchParams(query || '');
    }
    else {
      // Unknown URL format: url
      return null;
    }

    // Convert search params to object
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    return {
      path: pathname,
      params,
    };
  } catch (error) {
    // [DeepLink] Failed to parse URL (suppressed)
    return null;
  }
}

/**
 * Map deep link paths to app routes
 */
export function mapDeepLinkToRoute(deepLink: DeepLinkRoute): string {
  const { path, params } = deepLink;

  // Normalize the path
  const normalizedPath = path.toLowerCase().replace(/^\/+|\/+$/g, '');

  switch (normalizedPath) {
    case '':
    case 'home':
      return '/';

    case 'forecast':
      // Support location parameter: sunsquatcher://forecast?lat=40.7128&lng=-74.0060
      if (params.lat && params.lng) {
        return `/forecast?lat=${params.lat}&lng=${params.lng}`;
      }
      // Support location ID: sunsquatcher://forecast?location=new-york
      if (params.location) {
        return `/forecast?location=${params.location}`;
      }
      return '/forecast';

    case 'map':
      // Support center coordinates: sunsquatcher://map?lat=40.7128&lng=-74.0060&zoom=12
      if (params.lat && params.lng) {
        const zoom = params.zoom || '10';
        return `/map?lat=${params.lat}&lng=${params.lng}&zoom=${zoom}`;
      }
      return '/map';

    case 'photo-analyzer':
    case 'photo':
    case 'analyzer':
      return '/photo-analyzer';

    case 'notifications':
    case 'settings/notifications':
      return '/notifications';

    case 'profile':
    case 'settings':
      return '/profile';

    case 'locations':
    case 'saved':
    case 'favorites':
      return '/locations';

    case 'compare':
      return '/compare';

    case 'login':
    case 'signin':
      return '/login';

    case 'signup':
    case 'register':
      return '/signup';

    case 'privacy':
    case 'privacy-policy':
      return '/privacy';

    case 'terms':
    case 'terms-of-service':
    case 'tos':
      return '/terms';

    default:
      // Unknown path: path
      return '/';
  }
}

/**
 * Handle an incoming deep link
 */
function handleDeepLink(event: URLOpenListenerEvent): void {
  // Deep link received: event.url

  const route = parseDeepLink(event.url);
  if (!route) {
    // [DeepLink] Could not parse URL (warn suppressed)
    return;
  }

  if (deepLinkHandler) {
    deepLinkHandler(route);
  } else {
    // Store pending deep link to be handled when handler is registered
    // [DeepLink] No handler registered, storing for later (log suppressed)
    pendingDeepLink = event;
  }
}

/**
 * Initialize deep linking listeners
 */
export async function initializeDeepLinking(): Promise<void> {
  if (isInitialized) {
    // [DeepLink] Already initialized (log suppressed)
    return;
  }

  if (!isNative) {
    // [DeepLink] Not available on web platform (log suppressed)
    isInitialized = true;
    return;
  }

  // Listen for deep links when app is running
  await App.addListener('appUrlOpen', handleDeepLink);

  // Check if app was launched with a deep link
  const launchUrl = await App.getLaunchUrl();
  if (launchUrl?.url) {
    handleDeepLink({ url: launchUrl.url });
  }

  isInitialized = true;
}

/**
 * Register a handler for deep links
 */
export function registerDeepLinkHandler(handler: DeepLinkHandler): () => void {
  deepLinkHandler = handler;

  // Handle any pending deep link
  if (pendingDeepLink) {
    // Processing pending deep link
    handleDeepLink(pendingDeepLink);
    pendingDeepLink = null;
  }

  // Return cleanup function
  return () => {
    deepLinkHandler = null;
  };
}

/**
 * Create a deep link URL for sharing
 */
export function createDeepLink(
  path: string,
  params?: Record<string, string>,
  useUniversalLink = false
): string {
  const cleanPath = path.replace(/^\/+/, '');
  const queryString = params
    ? '?' + new URLSearchParams(params).toString()
    : '';

  if (useUniversalLink) {
    return `https://${WEB_DOMAIN}/${cleanPath}${queryString}`;
  }

  return `${APP_URL_SCHEME}://${cleanPath}${queryString}`;
}

/**
 * Clean up deep linking listeners
 */
export async function cleanupDeepLinking(): Promise<void> {
  if (!isNative) {
    return;
  }

  try {
    await App.removeAllListeners();
    deepLinkHandler = null;
    pendingDeepLink = null;
    isInitialized = false;
    // Cleaned up listeners
  } catch (error) {
    // [DeepLink] Failed to cleanup (suppressed)
  }
}

/**
 * Open external URL
 */
export async function openExternalUrl(url: string): Promise<void> {
  if (isNative) {
    // Use system browser on native via Browser plugin or fallback
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
    } catch {
      // Fallback to window.open if Browser plugin not available
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } else {
    // Use window.open on web
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Get the current app state
 */
export async function getAppState(): Promise<{ isActive: boolean }> {
  if (!isNative) {
    return { isActive: document.visibilityState === 'visible' };
  }

  try {
    const state = await App.getState();
    return state;
  } catch (error) {
    // [DeepLink] Failed to get app state (suppressed)
    return { isActive: true };
  }
}

/**
 * Listen for app state changes (foreground/background)
 */
export async function addAppStateChangeListener(
  callback: (state: { isActive: boolean }) => void
): Promise<() => void> {
  if (!isNative) {
    // Use visibility change event on web
    const handler = () => {
      callback({ isActive: document.visibilityState === 'visible' });
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }

  try {
    const listener = await App.addListener('appStateChange', callback);
    return () => listener.remove();
  } catch (error) {
    // [DeepLink] Failed to add state change listener (suppressed)
    return () => {};
  }
}

/**
 * Exit the app (Android only)
 */
export async function exitApp(): Promise<void> {
  if (isNative && Capacitor.getPlatform() === 'android') {
    await App.exitApp();
  }
}
